import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { toSitePath } from '@/lib/siteDomain';

// Compact multilingual switcher for SEO landing pages — dropdown style
// matching the /start page. Reads the hreflang <link rel="alternate"> tags
// that each page injects in its useEffect (correct per-language URLs with
// proper slugs/paths) and renders a button that opens a language menu.
// A MutationObserver catches late-injected tags (data-backed pages inject
// them only after the async DB fetch resolves).
// All 7 languages now have proper path-based cross-domain routes, so the
// switcher can offer the full set (PT/EN/ES/FR/DE/IT/NL).
const LANGS = [
  { code: 'pt', label: 'Português', short: 'PT', flag: '🇵🇹' },
  { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
  { code: 'es', label: 'Español', short: 'ES', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', short: 'FR', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', short: 'DE', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', short: 'IT', flag: '🇮🇹' },
  { code: 'nl', label: 'Nederlands', short: 'NL', flag: '🇳🇱' },
];

export default function SeoLangSwitcher({ currentLang }) {
  const [urls, setUrls] = useState({});
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const read = () => {
      const map = {};
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => {
        const hl = n.getAttribute('hreflang') || '';
        const href = n.getAttribute('href') || '';
        if (!href || hl === 'x-default') return;
        const code = hl.split('-')[0];
        if (!map[code] || hl === code) map[code] = href;
      });
      setUrls(map);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.head, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, [currentLang, window.location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const current = LANGS.find((l) => l.code === currentLang) || LANGS[0];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={current.label}
        aria-expanded={open}
        className="px-2.5 py-1.5 rounded-lg bg-white/15 backdrop-blur-md hover:bg-white/25 text-white text-xs font-bold flex items-center gap-1.5 transition-colors border border-white/20"
      >
        <span aria-hidden="true" className="text-sm leading-none">{current.flag}</span>
        {current.short}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
          {LANGS.map((l) => {
            const href = urls[l.code];
            const path = href ? toSitePath(href) : `${window.location.pathname}?lang=${l.code}`;
            const isActive = currentLang === l.code;
            return (
              <Link
                key={l.code}
                to={path}
                onClick={() => setOpen(false)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors ${
                  isActive ? 'text-[#076f77] bg-gray-50' : 'text-gray-700'
                }`}
              >
                <span aria-hidden="true" className="text-base leading-none">{l.flag}</span>
                {l.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}